import React, { useState } from 'react'
import { Map, InfoWindow, Marker, GoogleApiWrapper } from 'google-maps-react'
import { PrismicRichText } from '@prismicio/react'
import { airportLocation } from '../utils/airportLocation'

import Star from './svg/star'
import Clock from './svg/clock'
import Walk from './svg/walk'
import FlightTakeOff from './svg/flightTakeOff'
import Handshake from './svg/handshake'

function MapOfLocations({ slice, google, context }) {
  const [isActiveMarker, setActiveMarker] = useState({})
  const [isSelectedPlace, setSelectedPlace] = useState({})
  const [isShowingInfoWindow, setShowingInfoWindow] = useState(false)

  const onMarkerClick = (props, marker) => {
    setActiveMarker(marker)
    setSelectedPlace(props)
    setShowingInfoWindow(true)
  }

  const onInfoWindowClose = () => {
    setActiveMarker(null)
    setShowingInfoWindow(false)
  }

  const onMapClicked = () => {
    if (isShowingInfoWindow) {
      setActiveMarker(null)
      setShowingInfoWindow(false)
    }
  }

  const containerStyle = {
    position: 'relative',
    width: '100%',
    minHeight: '600px',
    height: '100%'
  }

  const data = context.productData
  const airportCoooridnates = airportLocation(context.airport)
  if (!data || data.edges.length === 0) return null
  return (
    <section className='mx-auto max-w-screen-xl py-6 px-4'>
      {slice.primary.map_title.text && (
        <div className='prose prose-lg mx-auto max-w-none lg:prose-xl'>
          <PrismicRichText field={slice.primary.map_title.richText} />
        </div>
      )}
      <div className='relative mt-4 flex flex-col rounded-t bg-white p-2'>
        <Map
          containerStyle={containerStyle}
          google={google}
          zoom={13}
          initialCenter={{
            lat: airportCoooridnates.latitude,
            lng: airportCoooridnates.longitude
          }}
          onClick={onMapClicked}
          zoomControl={true}
          mapTypeControl={false}
          scaleControl={false}
          streetViewControl={false}
          panControl={false}
          scrollwheel={false}
          draggable={true}>
          {data.edges.map((parking, index) => {
            var parkingData = parking.node
            const position = { lat: parkingData.latitude, lng: parkingData.longitude }
            const cleanName = parkingData.name
              .split('-')[0]
              .replace(/[^a-zA-Z0-9 ]/g, '')
              .trim()
              .split(' ')
              .join('-')
              .toLowerCase()
            const url = './' + cleanName + '/'
            return (
              <Marker
                key={`${parkingData.type}-${index}`}
                id={parkingData.id}
                onClick={onMarkerClick}
                name={parkingData.name}
                address={parkingData.address}
                image={parkingData.logo}
                link={url}
                position={position}
                icon={{
                  url: parkingData.logo.url + '?border=3,FFFFFF&fit=clip&w=100&pad=3'
                }}
              />
            )
          })}
          <InfoWindow marker={isActiveMarker} onClose={onInfoWindowClose} visible={isShowingInfoWindow}>
            <div style={{ maxWidth: '300px' }}>
              {isSelectedPlace.icon && <img style={{ width: 'auto', margin: 'auto' }} src={isSelectedPlace.icon.url} alt={isSelectedPlace.name} />}
              {isSelectedPlace.name && <h1 style={{ fontWeight: 'bold', borderBottom: 'solid 1px #e9892c', paddingBottom: '.25em', paddingTop: '1em', fontSize: '20px' }}>{isSelectedPlace.name}</h1>}
              {isSelectedPlace.address && <div style={{ margin: '.5em 0', fontSize: '16px', marginTop: '.5em' }} dangerouslySetInnerHTML={{ __html: isSelectedPlace.address }} />}
              {isSelectedPlace.link && (
                <a style={{ color: '#e9892c', fontSize: '16px', fontWeight: 'bold' }} href={isSelectedPlace.link}>
                  Read More
                </a>
              )}
            </div>
          </InfoWindow>
        </Map>
      </div>
      <div className='divide-y rounded-b bg-white'>
        {data.edges.map((parking, index) => {
          var parkingData = parking.node
          const cleanName = parkingData.name
            .split('-')[0]
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .trim()
            .split(' ')
            .join('-')
            .toLowerCase()
          const url = './' + cleanName + '/'
          let stars = []
          if (parkingData.star_rating) {
            for (let i = 0; i < parkingData.star_rating; i++) {
              stars.push(<Star key={`star-${i}`} className='h-4 w-4 pb-px ' />)
            }
          }
          return (
            <div className='group relative flex flex-row flex-wrap py-2 shadow hover:bg-orange-50 md:py-4' key={`${parkingData.type}-${index}`}>
              {parkingData.logo && (
                <div className='text-center'>
                  <img src={parkingData.logo.url} className='ml-2 h-auto w-20 rounded-sm border p-1 md:ml-4 md:w-32' alt={`${parkingData.name} logo` || ''} />
                </div>
              )}
              <div className='flex w-full flex-1 flex-col md:flex-row'>
                {parkingData.name && (
                  <>
                    <div className='flex items-center px-2 md:w-1/2 md:px-4'>
                      <h2 className='mb-2 flex font-serif text-xl text-orange-500'>
                        {parkingData.name}
                        {stars.length > 0 && <span className='my-2 flex md:ml-2'>{stars}</span>}
                      </h2>
                    </div>
                    <div>{parkingData.sellpoint}</div>
                  </>
                )}
                <div className='flex flex-col items-start justify-center gap-2 px-2 md:px-4'>
                  {!parkingData.meet_and_greet && (
                    <>
                      {parkingData.distance_miles && !parkingData.on_airport && <div>{parkingData.distance_miles && `${parkingData.distance_miles} miles from ${parkingData.airport}`}</div>}
                      {parkingData.on_airport === true && (
                        <div className='flex'>
                          <Walk className='h-6 w-6 text-orange-500 group-hover:text-orange-600' />
                          On Airport
                        </div>
                      )}
                      {(parkingData.transfer_frequency === 0 || parkingData.transfer_frequency === '0') && (
                        <div className='flex'>
                          <Walk className='h-6 w-6 text-orange-500 group-hover:text-orange-600' />
                          Walking distance
                        </div>
                      )}
                      {parkingData.transfer_frequency !== '0' && parkingData.transfer_time && (
                        <div className='flex'>
                          <Clock className='h-6 w-6 text-orange-500 group-hover:text-orange-600' />
                          {`${parkingData.transfer_time} min transfer`}
                        </div>
                      )}
                    </>
                  )}
                  {parkingData.meet_and_greet && (
                    <div className='flex items-center gap-1'>
                      <Handshake className='h-6 w-6 text-orange-500 group-hover:text-orange-600' />
                      <div>Meet and Greet</div>
                    </div>
                  )}

                  <div className='flex items-center gap-1'>
                    <FlightTakeOff className='h-6 w-6 text-orange-500 group-hover:text-orange-600' />
                    <div>{parkingData.terminal ? `Terminal ${parkingData.terminal}` : 'All Terminals'}</div>
                  </div>
                </div>
                <div className='mr-2 flex flex-1 items-center justify-end text-right md:mr-4'>
                  <a className='border-b border-blue-500 text-blue-500 group-hover:border-transparent group-hover:text-blue-900' href={url}>
                    Read More
                    <span className='absolute inset-0' aria-hidden='true'></span>
                  </a>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

const LoadingContainer = (props) => (
  <svg className='-ml-1 mr-3 h-5 w-5 animate-spin text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
  </svg>
)

export default GoogleApiWrapper({
  apiKey: process.env.GATSBY_GOOGLE_API_KEY,
  LoadingContainer: LoadingContainer
})(MapOfLocations)
